import { BlueprintVertex } from '../vertex.js';

import { fromTiptap } from '../tiptap-wysiwyg.js';

import * as constants from '../constants.js';
/** @todo finish shape class, maybe allow passing vertex instances based on their type */
export class BlueprintShape extends BlueprintVertex {
  /** @type {fabric.Textbox|null} */
  textRef = null;

  /**
   * @todo Make types for these config objects
   * @param {fabric.Canvas} canvas - the fabric canvas
   * @param {Object} config - vertex configuration
   * @param {import ("../blueprint-graph.js").BPShapeKey} config.shapeType - a valid Blueprint shape type to generate a shape
   * @param {fabric.Object} [config.fabricObject] - an existing fabric object.
   * @param {Object=} config.fabricOptions - shape options to be passed into fabric
   * @param {Object=} textConfig - shape text configuration
   * @returns {BlueprintShape}
   */
  constructor(
    canvas,
    { shapeType, fabricObject, fabricOptions = {} },
    textConfig,
  ) {
    super(canvas, { vertexType: constants.CELL_TYPES.shape });

    // see if a shape string is being passed or a fabric object
    if (fabricObject) {
      // if it's a fabric object, just set the ref and return this
      this.ref = fabricObject;
      this.shapeType = Object.keys(constants.SHAPE_TYPES).find(
        key => constants.SHAPE_TYPES[key].toLowerCase() === fabricObject.type,
      );

      /** @todo Move these values so we're not repeating specific strings in different places */
      this.defaultTextColor = fabricObject.bpShapeTextFill || 'black';
      this.defaultTextAlign = fabricObject.bpShapeTextAlign || 'center';

      if (fabricObject.bpShapeText) {
        this.addText({
          text: fabricObject.bpShapeText,
          styles: fabricObject.bpShapeTextStyles,
          fill: fabricObject.bpShapeTextFill,
          textAlign: fabricObject.bpShapeTextAlign,
        });
      }
      return;
    }

    const { position = {}, ...opts } = fabricOptions;

    /** @todo: NEED to assign public properties for usage outside class other than using `ref` for everything, maybe make ref priate altogether */
    const shapeOptions = {
      angle: 0,
      snapAngle: 22.5,
      strokeUniform: true,
      originX: 'center',
      originY: 'center',
      top: position.y || 0,
      left: position.x || 0,
      ...opts,
    };

    let shapeClass = constants.SHAPE_TYPES[shapeType];
    let shape;

    if (shapeType.toLowerCase() === 'circle') {
      /** @todo: Do we respect width or height if passed in? */
      const radius = opts.width
        ? opts.width / 2
        : Math.round(canvas.getHeight() / 8);

      shapeClass = 'Circle';
      // @ts-ignore
      shapeOptions.radius = radius;
    }

    if (shapeType.toLowerCase() === 'diamond') {
      // remove existing points so they're not immediately overriden after creating the polygon via the options parameter
      delete shapeOptions.points;

      /** @todo: This expects width to be present, other shapes don't */
      shape = new fabric.Polygon(
        [
          { x: shapeOptions.width / 2, y: 0 },
          { x: shapeOptions.width, y: shapeOptions.height / 2 },
          { x: shapeOptions.width / 2, y: shapeOptions.height },
          { x: 0, y: shapeOptions.height / 2 },
        ],
        shapeOptions,
      );
    } else {
      shape = new fabric[shapeClass](shapeOptions);
    }

    /** @type fabric.Object */
    this.ref = shape;

    if (textConfig?.text) {
      this.addText(textConfig);
    }
    this.defaultTextColor = textConfig?.fill || 'black';

    this.defaultTextAlign = textConfig?.textAlign || 'center';

    // if (position) this.ref.setPositionByOrigin(position, 'center', 'center');

    /** @type import("../blueprint-graph.js").BPShapeKey */
    this.shapeType = shapeType;
  }

  /**
   * @param {string} value - An rgb(a) or hex color
   */
  set fill(value) {
    this.ref.set('fill', value);
  }

  /**
   * @property {string} fill - The shape's fill color
   * @returns {string} An rgb(a) or hex color
   */
  get fill() {
    return this.ref.fill;
  }

  /**
   * @property {boolean} active - The shape's fill color
   * @param {boolean} value - Whether to select this shape or not
   */
  set active(value) {
    /** @todo: I don't know if this even works, probably need to trigger a render immediately here. This should either be removed or fixed and maybe changed to a method?  */
    this.ref.set('active', value);
  }

  /**
   * @todo: Don't think this is necessary
   * @returns {fabric.Object}
   */
  get fabricObject() {
    return this.ref;
  }

  get clonableProps() {
    const {
      strokeWidth,
      stroke,
      strokeDashArray,
      angle,
      fill,
      width,
      height,
      radius,
      points,
      scaleX,
      scaleY,
    } = this.ref;

    return {
      strokeWidth,
      stroke,
      strokeDashArray,
      angle,
      fill,
      width,
      height,
      radius,
      points,
      scaleX,
      scaleY,
    };
  }

  /** Clone this shape by returning a new instance */
  clone(shapeType = this.shapeType, replace = false) {
    const { canvas, ref } = this;
    const fabricOptions = replace
      ? {
          ...this.clonableProps,
          top: ref.top,
          left: ref.left,
        }
      : this.clonableProps;

    const newShape = new BlueprintShape(
      canvas,
      { shapeType, fabricOptions },
      {
        text: this.text,
        styles: this.textStyles,
        fill: this.textColor,
        textAlign: this.textAlign,
      },
    );

    if (!replace)
      newShape.setCenterPoint({ x: ref.left + 20, y: ref.top + 20 });

    return newShape;
  }

  /** Replace this shape by returning a new one */
  toShape(newShapeType) {
    if (this.shapeType === newShapeType) return this;
    const newShape = this.clone(newShapeType, true);
    this.canvas.remove(this.ref, this.textRef);
    return newShape;
  }

  updateFromTiptap = content => {
    if (!this.textRef) this.addText(fromTiptap(content));
    else {
      this.textRef.set(fromTiptap(content));
    }

    this.#updateCustomFabricProps({
      text: this.text,
      styles: this.textStyles,
      fill: this.textColor,
    });

    this.render();
  };

  get textStyles() {
    return this.textRef?.styles || {};
  }

  get text() {
    return this.textRef?.text || '';
  }

  get textColor() {
    return this.textRef?.fill || this.defaultTextColor;
  }

  set textColor(value) {
    if (!this.textRef) return;
    this.textRef.set('fill', value);
    this.#updateCustomFabricProps({ fill: value });
    this.render();
  }

  get textAlign() {
    return this.textRef?.textAlign || this.defaultTextAlign;
  }

  set textAlign(value) {
    if (!this.textRef) return;
    this.textRef.set('textAlign', value);
    this.#updateCustomFabricProps({ textAlign: value });
    this.render();
  }

  #updateCustomFabricProps = ({
    text = this.textRef?.get('text') || this.ref.get('bpShapeText'),
    styles = this.textRef?.get('styles') || this.ref.get('bpShapeTextStyles'),
    fill = this.textRef?.get('fill') || this.ref.get('bpShapeTextFill'),
    textAlign = this.textRef?.get('textAlign') || this.ref.get('textAlign'),
  }) => {
    // Export variables for save/load
    this.ref.set({
      bpShapeText: text,
      bpShapeTextStyles: styles,
      bpShapeTextFill: fill,
      bpShapeTextAlign: textAlign,
    });
  };

  addText = ({ text, styles = {}, fill: textFill, textAlign: align }) => {
    if (this.textRef) return;

    const { canvas } = this;
    const fill = textFill || this.defaultTextColor;
    const textAlign = align || this.defaultTextAlign;

    this.textRef = new fabric.Textbox(canvas, {
      text,
      styles,
      fill,
      textAlign,
      fontFamily: '"Open Sans", sans-serif',
      top: this.ref.top,
      left: this.ref.left,
      originX: 'center',
      originY: 'center',
      lockScalingY: true,
      selectable: false,
      hasControls: false,
      hasBorders: false,
      width: this.ref.width * this.ref.scaleX,
      fontSize: 14,
      splitByGrapheme: true,
      evented: false,
      excludeFromExport: true,
    });

    this.#updateCustomFabricProps({ text, styles, fill, textAlign });

    if (canvas.getObjects().includes(this.ref)) {
      canvas.add(this.textRef);
    } else {
      canvas.on('object:added', ({ target }) => {
        if (target === this.ref) canvas.add(this.textRef);
      });
    }

    // Ensure the text is placed on the "same" layer as (above) the ref
    canvas.moveTo(this.textRef, canvas.getObjects().indexOf(this.ref) + 1);

    /** @todo fix group movement */
    canvas.on('object:moving', ({ target }) => {
      if (target === this.ref) {
        this.textRef
          .set({
            top: this.ref.top,
            left: this.ref.left,
          })
          .setCoords();
      } else if (
        target._objects?.some(
          obj => JSON.stringify(obj) === JSON.stringify(this.ref),
        )
      ) {
        this.textRef
          .set({
            top: this.ref.top + target.top + target.height / 2,
            left: this.ref.left + target.left + target.width / 2,
          })
          .setCoords();
      }
    });
    canvas.on('object:scaling', ({ target }) => {
      if (target === this.ref) {
        this.textRef
          .set({
            top: this.ref.top,
            left: this.ref.left,
            width: this.ref.width * this.ref.scaleX,
          })
          .setCoords();
      } else if (
        target._objects?.some(
          obj => JSON.stringify(obj) === JSON.stringify(this.ref),
        )
      ) {
        this.textRef
          .set({
            top:
              this.ref.top + target.top + (target.height * target.scaleY) / 2,
            left:
              this.ref.left + target.left + (target.width * target.scaleX) / 2,
            width: this.ref.width * target.scaleX,
            height: this.ref.height * target.scaleY,
          })
          .setCoords();
      }
    });
    canvas.on('object:rotating', ({ target }) => {
      if (target === this.ref) {
        this.textRef
          .set({
            angle: target.angle,
          })
          .setCoords();
      }
    });
    canvas.on('object:removed', ({ target }) => {
      if (target === this.ref) {
        canvas.remove(this.textRef);
      }
    });
  };

  /**
   * Position the shape by it's center
   * This is a method because getters/setters aren't inherited in JS
   * @param {{ x: number, y: number }} point
   */
  setCenterPoint(point) {
    this.ref.setPositionByOrigin(point, 'center', 'center');
    this.textRef?.setPositionByOrigin(point, 'center', 'center');
  }
}
